/**
 * Inspection domain types — check-in / check-out vehicle inspections.
 *
 * Maps snake_case DB columns to camelCase domain types.
 * Used by inspection.service.ts, use-inspection.ts, and screen components.
 */

// ─── Enums & Constants ────────────────────────────────────────────────────

export type InspectionType = 'checkin' | 'checkout'

export type ConditionGrade = 'excellent' | 'good' | 'fair' | 'poor'

export const CONDITION_GRADES: ConditionGrade[] = ['excellent', 'good', 'fair', 'poor'] as const

/**
 * Las SEIS que hay que tomar sí o sí, y por qué son estas seis.
 *
 * Antes eran las quince: `MIN_INSPECTION_PHOTOS` salía del largo de la lista
 * completa, así que "el mínimo" y "todas" eran la misma cosa. Quince fotos por
 * lado son treinta por viaje, entre dos personas paradas en la calle — y los
 * dos únicos viajes reales de Tago se hicieron por WhatsApp, sin una sola foto.
 * Quince fotos que empujan a la gente fuera de la app protegen menos que seis
 * que sí se toman.
 *
 * La selección no es "las seis primeras". Son las que sostienen un reclamo:
 *
 *   - Las cuatro ESQUINAS en 3/4 (no los cuatro costados planos): cada una ve
 *     dos caras del carro, así que entre las cuatro cubren la carrocería
 *     completa CON SOLAPE. Es la vuelta que uno da caminando alrededor, y es lo
 *     que hace cualquier arrendadora seria.
 *   - `odometer` y `dashboard`: los dos números que mueven plata. El viaje del
 *     H1 se cerró SIN lectura de odómetro en ninguna de las dieciocho fotos,
 *     así que no hay kilometraje de salida ni de entrada, y el combustible
 *     tampoco se puede comparar. Eso no vuelve a pasar.
 *
 * Las otras nueve siguen estando en la cuadrícula y se pueden tomar; lo que no
 * hacen es bloquear la entrega.
 */
export const REQUIRED_INSPECTION_PHOTO_AREAS = [
  'front_left',
  'front_right',
  'rear_left',
  'rear_right',
  'odometer',
  'dashboard',
] as const

/** Se pueden tomar y suman evidencia, pero no bloquean el traspaso. */
export const OPTIONAL_INSPECTION_PHOTO_AREAS = [
  'front',
  'back',
  'left',
  'right',
  'windshield',
  'front_seats',
  'rear_seats',
  'trunk',
  'tires',
] as const

/**
 * Todo lo que la cuadrícula ofrece, con las obligatorias primero para que la
 * persona empiece por lo que de verdad hay que tomar.
 */
export const INSPECTION_PHOTO_AREAS = [
  ...REQUIRED_INSPECTION_PHOTO_AREAS,
  ...OPTIONAL_INSPECTION_PHOTO_AREAS,
] as const

/**
 * La foto de la licencia, EN VIVO, al momento de recibir el carro.
 *
 * QUE ES Y QUE NO ES. No es verificar la licencia: eso ya pasó una sola vez en
 * el onboarding, contra SERTRACEN, y queda en `driver_licenses`. Esto es dejar
 * CONSTANCIA de que el huésped traía su licencia encima antes de subirse al
 * carro — que es una cosa distinta y es la que importa el día del traspaso. Una
 * licencia aprobada hace tres meses no prueba que hoy la ande.
 *
 * Por eso no llama a ningún proveedor, no espera respuesta de nadie y no puede
 * fallar por una red lenta: se toma la foto y se sigue. Fricción cero.
 *
 * Vive FUERA de `INSPECTION_PHOTO_AREAS` a propósito:
 *   - `MIN_INSPECTION_PHOTOS` sigue contando las fotos DEL CARRO, así que la
 *     devolución no hereda un requisito que no le corresponde.
 *   - No aparece en la cuadrícula del vehículo, donde "marcar daño" no tendría
 *     ningún sentido sobre el documento de una persona.
 * Pero SÍ comparte el tipo `InspectionPhotoArea`, así que reutiliza tal cual la
 * cámara en vivo, el almacenamiento durable y la subida. Ni una ruta nueva.
 */
export const LICENSE_PHOTO_AREA = 'license' as const

export type InspectionPhotoArea =
  | (typeof INSPECTION_PHOTO_AREAS)[number]
  | typeof LICENSE_PHOTO_AREA

/**
 * Cuántas fotos del carro hacen falta.
 *
 * Sale del largo del conjunto OBLIGATORIO, no de un literal: mover una foto de
 * obligatoria a opcional cambia este número solo, sin tocar nada más.
 *
 * OJO, y es la razón por la que existe `faltanAreasObligatorias`: mientras
 * fueron quince de quince, "contar seis" y "tener las seis" eran equivalentes.
 * Ya no. Contar sola dejaría pasar una entrega con seis fotos de asientos y
 * cero de la carrocería, que es exactamente lo que no queremos. La cuenta sirve
 * para la barra de progreso; lo que decide es el CONJUNTO.
 */
export const MIN_INSPECTION_PHOTOS = REQUIRED_INSPECTION_PHOTO_AREAS.length

/**
 * Whether a displayed photo slot blocks the handover.
 *
 * The licence is intentionally outside the vehicle-photo list so it can never
 * satisfy the six-angle vehicle requirement. It is nevertheless mandatory
 * whenever its one-slot pickup grid is shown to a guest.
 */
export function isInspectionPhotoRequired(area: InspectionPhotoArea): boolean {
  return area === LICENSE_PHOTO_AREA || REQUIRED_INSPECTION_PHOTO_AREAS.includes(area as never)
}

/** Las obligatorias que todavía no se han tomado. Vacío = se puede enviar. */
export function faltanAreasObligatorias(
  areasTomadas: readonly InspectionPhotoArea[],
): readonly (typeof REQUIRED_INSPECTION_PHOTO_AREAS)[number][] {
  const tomadas = new Set(areasTomadas)
  return REQUIRED_INSPECTION_PHOTO_AREAS.filter((area) => !tomadas.has(area))
}

type InspectionCaptureSource = 'tago_live_camera'

// ─── Form Data (local state before submission) ────────────────────────────

export interface InspectionPhoto {
  uri: string
  area: InspectionPhotoArea
  width: number
  height: number
  capturedAt: string
  source: InspectionCaptureSource
  latitude?: number
  longitude?: number
  locationAccuracyMeters?: number
  /**
   * Metadatos EXIF leídos de la captura en vivo (tiempo y GPS embebidos por la
   * cámara). Se guardan en `verification_payload.exif` y el ajustador valida
   * que el tiempo coincida con `capturedAt`: la evidencia queda atada al
   * momento y al lugar por la propia imagen, no solo por la fila.
   */
  exif?: {
    dateTimeOriginal?: string
    latitude?: number
    longitude?: number
  }
  /**
   * Esta foto muestra un daño.
   *
   * La columna `is_damage` existe en `rental_inspection_images` desde el
   * principio y el servicio la escribía FIJA EN FALSE, así que nunca hubo una
   * sola foto marcada. Reportar un daño solo era posible con la renta ya
   * `completed`, abriendo una disputa — o sea, después de devolver el carro.
   *
   * Un huésped que llega y encuentra un golpe no tiene que abrir una disputa:
   * tiene que poder señalarlo antes de arrancar, sobre la foto que lo muestra.
   * La marca vive en la foto y no en un formulario aparte porque la evidencia y
   * la afirmación son la misma cosa.
   */
  isDamage?: boolean
}

export interface InspectionFormData {
  /** Fuel gauge percentage 0-100, step 5 */
  fuelLevel: number
  /** Odometer reading in km */
  odometerKm: number
  /** Exterior body condition */
  exteriorCondition: ConditionGrade
  /** Interior cabin condition */
  interiorCondition: ConditionGrade
  /** Free-text observations, max 500 chars */
  notes: string
  /** Photos taken for each required angle */
  photos: InspectionPhoto[]
  /**
   * Las dos firmas de la entrega, como SVG.
   *
   * Opcionales en el tipo y obligatorias en la pantalla, a propósito: hay
   * inspecciones sembradas y reintentos que no las traen, y hacerlas
   * obligatorias acá rompería un reintento de subida de fotos por un campo que
   * no cambió. Lo que no puede pasar es que una entrega NUEVA se cierre sin
   * ellas, y eso lo impide el formulario.
   */
  guestSignatureSvg?: string | null
  hostSignatureSvg?: string | null
}

// ─── Domain Models (from DB) ──────────────────────────────────────────────

export interface InspectionImageItem {
  id: string
  inspectionId: string
  url: string
  area: string
  storagePath: string
  isDamage: boolean
  caption: string | null
  captureSource: string | null
  capturedAt: string | null
  photoSha256: string | null
  sequenceIndex: number | null
  latitude: number | null
  longitude: number | null
  locationAccuracyMeters: number | null
  verificationPayload: Record<string, unknown>
}

/**
 * `inspection_assurance_reviews.status` is a plain text column, so the value
 * list lives here and doubles as the runtime guard used when reading rows.
 */
export const INSPECTION_ASSURANCE_STATUSES = [
  'pending_submission',
  'submitted',
  'under_review',
  'accepted',
  'rejected',
  'needs_action',
  'expired',
  'failed',
] as const

export type InspectionAssuranceStatus = (typeof INSPECTION_ASSURANCE_STATUSES)[number]

export interface InspectionAssuranceReview {
  id: string
  inspectionId: string
  rentalId: string
  inspectionType: InspectionType
  partner: string
  partnerSubmissionId: string | null
  status: InspectionAssuranceStatus
  submittedAt: string | null
  dueAt: string
  completedAt: string | null
  reportUrl: string | null
  reportStoragePath: string | null
  resultSummary: string | null
  rejectionReasons: string[]
  photoCount: number
  evidenceHash: string | null
  lastError: string | null
  lastSyncedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface InspectionDetail {
  id: string
  rentalId: string
  inspectionType: InspectionType
  performedBy: string
  fuelLevel: number
  odometerKm: number
  exteriorCondition: ConditionGrade | null
  interiorCondition: ConditionGrade | null
  notes: string | null
  /**
   * Las dos firmas, listas para mostrarse.
   *
   * Se exigían en la pantalla, se subían al bucket y se escribían en la fila
   * — y NINGUNA pantalla las leía. `mapInspectionRow` ni siquiera las
   * mapeaba, así que eran invisibles desde el dominio: una firma que nadie
   * puede ver no es una firma, es un archivo.
   *
   * Vienen firmadas (URL temporal) porque el bucket es privado.
   */
  guestSignatureUrl: string | null
  hostSignatureUrl: string | null
  images: InspectionImageItem[]
  assuranceReview: InspectionAssuranceReview | null
  createdAt: string
}

// ─── Checkout Comparison ──────────────────────────────────────────────────

export interface CheckoutComparison {
  checkin: InspectionDetail
  checkout: InspectionDetail
  /** Positive = more fuel at checkout, negative = less */
  fuelDifference: number
  /** Kilometers driven during the rental */
  kmDriven: number
}
